import produce from 'immer'

import { isProduction } from 'utils/nodeEnv'

import * as T from 'types'
import * as E from 'types/enums'

import { api } from './api'
import { refreshCompletionPercentages } from './swr/completionPercentages'

/** **** data utils **** */
const createServiceItem = (serviceId: string, quantity: number) => ({
  type: serviceId,
  quantity,
})

// Creates an individual cart item
const createNewCartItem = (listingId: string, serviceId: string, quantity: number) => ({
  listing: listingId,
  services: [createServiceItem(serviceId, quantity)],
})

// remove the current pricing packages if a different one is added
const filterPackageServices = (services: (T.IOrderService | T.ICreateOrderService)[]) =>
  services.filter(service => {
    if (typeof service.type !== 'string' && service.type.status === 'PricingPackage') return false

    return true
  })

const getIndexOfListingCart = (cart: T.ICreateOrder, listingId: string) => {
  if (!cart) return -1

  return cart.items.findIndex(
    items => typeof items.listing !== 'string' && items.listing._id === listingId,
  )
}

const getServiceIndex = (services: T.IOrderService[], serviceId: string) =>
  services.findIndex(service => {
    if (typeof service.type === 'string') return service.type === serviceId

    return service.type._id === serviceId
  })

/** **** CRUD functions **** */
// NB: it's error-prone to call this directly. use `addItemToCart` instead
export const updateCartItems = async (
  cart: T.ICreateOrder,
  listingId: string,
): Promise<T.IOrder | undefined> => {
  const response = await api.updateCartItems(cart._id, cart.items)
  if (!response.data || !response.ok) throw new Error(`bad response: ${response.problem}`)

  refreshCompletionPercentages(listingId)
  return response.data
}

export const updateShipping = async (
  cart: T.ICreateOrder | T.ICreateOrder,
  shipping: T.IOrderShipping,
): Promise<[T.IOrder | undefined, Error | undefined]> => {
  try {
    const response = await api.updateCartShipping(cart._id, shipping)
    if (!response.data || !response.ok) throw new Error(`bad response: ${response.problem}`)

    return [response.data, undefined]
  } catch (error) {
    if (!isProduction()) console.error(error)

    if (error instanceof Error) return [undefined, error]

    return [undefined, undefined]
  }
}

export const payCart = async (
  cartId: string,
  update: { stripeToken: string; discount?: T.IOrderDiscount },
): Promise<[T.IOrder | undefined, Error | undefined]> => {
  try {
    const response = await api.payCart(cartId, update)
    if (!response.data || !response.ok) throw new Error(`bad response: ${response.problem}`)

    return [response.data, undefined]
  } catch (error) {
    if (!isProduction()) console.error(error)

    if (error instanceof Error) return [undefined, error]

    return [undefined, undefined]
  }
}

/** **** prepropcessing functions **** */
const addToExisitingCart = (
  services: (T.IOrderService | T.ICreateOrderService)[],
  serviceId: string,
  quantity: number,
  type: string | T.IService,
) =>
  produce(services, draftServices => {
    if (type === 'PricingPackage') draftServices = filterPackageServices(services)

    const serviceIdx = draftServices.findIndex(
      service => typeof service.type !== 'string' && service.type._id === serviceId,
    )
    if (serviceIdx > -1) draftServices[serviceIdx].quantity += quantity
    else draftServices.push(createServiceItem(serviceId, quantity))

    return draftServices
  })

export const addItemToCart = async (
  cart: T.ICreateOrder,
  listingId: string,
  serviceId: string,
  type: string | T.IService,
  quantity = 1,
) => {
  const index = getIndexOfListingCart(cart, listingId)

  if (index > -1) {
    cart.items[index].services = addToExisitingCart(
      cart.items[index].services,
      serviceId,
      quantity,
      type,
    )
  } else {
    cart.items.push(createNewCartItem(listingId, serviceId, quantity))
  }

  const updatedCart = await updateCartItems(cart, listingId)
  return updatedCart
}

export const removeItemFromCart = async (cart: T.IOrder, listingId: string, serviceId: string) => {
  const index = getIndexOfListingCart(cart, listingId)
  if (index === -1 || !cart.items[index].services) return

  const cartServices = cart.items[index].services
  const serviceIdx = getServiceIndex(cartServices, serviceId)
  if (serviceIdx === -1) return

  cartServices.splice(serviceIdx, 1)

  // filter cart item if there are no services left
  if (!cartServices.length) cart.items.splice(index, 1)
  else cart.items[index].services = cartServices

  const updatedCart = await updateCartItems(cart, listingId)
  return updatedCart
}

export const removeYardSignServicesFromCart = async (cart: T.IOrder, listingId: string) => {
  const index = getIndexOfListingCart(cart, listingId)
  if (index === -1 || !cart.items[index].services) return

  const cartServices = cart.items?.[index]?.services?.filter(
    item => !item.type.type?.includes(E.ServiceType.YardSign),
  )
  cart.items[index].services = cartServices

  const updatedCart = await updateCartItems(cart, listingId)
  return updatedCart
}

// add package
export const addPackageToCart = async (
  cart: T.IOrder,
  plan: T.IPricingPackage | undefined,
  listingId: string,
) => {
  if (!plan) return cart // this should never happen, but typescript doesn't know that

  if (!plan.service) return cart

  // (NB: paid plans will be removed on the backend when user selects a different paid plan)
  const idx = getIndexOfListingCart(cart, listingId)

  // remove all package services before adding a different package
  if (idx > -1) {
    cart.items[idx].services = filterPackageServices(cart.items[idx].services) as T.IOrderService[]
  }

  /*
   * we don't add packages with no upfront fees to the cart, we only remove the paid package
   *
   * IMPORTANT: please do not remove/alter this code without speaking to a team lead. Doing so
   * will break a lot of the OrderSummary view in the app
   */
  const updatedCart = !plan.payNow
    ? await updateCartItems(cart, listingId)
    : await addItemToCart(cart, listingId, plan.service._id, 'PricingPackage', 1)

  return updatedCart
}
